/**
 * The end of Teslyn Support access on screen (decision 905; docs/specs/cloud/support-access.md §2.5). The database
 * refuses the next read the moment a grant ends, but a page keeps what it has already read: a map keeps its aircraft
 * until its next read succeeds. So while support access is open the Shell asks every 10 seconds whether it still stands
 * (a tenant admin's "End now") and at the known end time (it ran out), and on the end takes the page away.
 */
import { useEffect, useState } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import { checkSupport } from './api.ts';

/** How often the Shell asks whether a tenant admin has ended the access. */
export const SUPPORT_CHECK_MS = 10_000;

export interface SupportWatch {
  /** The access has ended: nothing of the company may stay on screen. */
  readonly ended: boolean;
  /** When it ends, as last answered (a newer grant in force moves it). */
  readonly endsAt: string | null;
}

/**
 * Watch the grant in force of `tenantId`; null for no watch (no support access, not opened yet, or the staging switch,
 * which no grant ends). At the known end, no answer counts as ended: the end has passed.
 */
export function useSupportWatch(
  sb: SupabaseClient,
  tenantId: string | null,
  firstEndsAt: string | null,
): SupportWatch {
  const [ended, setEnded] = useState(false);
  const [endsAt, setEndsAt] = useState(firstEndsAt);

  useEffect(() => {
    if (!tenantId) return undefined;
    let live = true;
    const ask = async (): Promise<void> => {
      const r = await checkSupport(sb, tenantId).catch(() => ({ state: 'unknown' }) as const);
      if (!live) return;
      if (r.state === 'ended') setEnded(true);
      if (r.state === 'in_force' && r.endsAt) setEndsAt(r.endsAt);
    };
    const t = setInterval(() => void ask(), SUPPORT_CHECK_MS);
    return () => {
      live = false;
      clearInterval(t);
    };
  }, [sb, tenantId]);

  useEffect(() => {
    if (!tenantId || !endsAt) return undefined;
    let live = true;
    const t = setTimeout(
      () => {
        void checkSupport(sb, tenantId)
          .catch(() => ({ state: 'unknown' }) as const)
          .then((r) => {
            if (!live) return;
            if (r.state === 'in_force' && r.endsAt) setEndsAt(r.endsAt);
            else setEnded(true);
          });
      },
      Math.max(0, Date.parse(endsAt) - Date.now()),
    );
    return () => {
      live = false;
      clearTimeout(t);
    };
  }, [sb, tenantId, endsAt]);

  return { ended, endsAt };
}
